import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useDispatch, useSelector } from "react-redux";

import { createReference } from "@medplum/core";
import { Observation, Questionnaire, QuestionnaireResponse } from "@medplum/fhirtypes";
import { useMedplum, useMedplumProfile } from "@medplum/react";

import type { UseFormReturnType } from "@mantine/form";

import { useTimerContext } from "@/components/Timer/TimerContext";
import {
  LABOR_AMEND_FLOWSHEET_EDIT_MISSING_RESPONSE_MESSAGE,
  LABOR_AMEND_FLOWSHEET_EDIT_MISSING_RESPONSE_TITLE,
  LABOR_AMEND_FLOWSHEET_SAVE_ERROR_EDIT_ROW_MESSAGE,
  LABOR_AMEND_FLOWSHEET_SAVE_ERROR_QUICK_ADD_MESSAGE,
  LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
  LABOR_AMEND_LABOR_STAGES_REFRESH_FAILED_MESSAGE,
  LABOR_AMEND_LABOR_STAGES_REFRESH_FAILED_TITLE,
  LABOR_AMEND_QUEUED_MESSAGE,
  LABOR_AMEND_QUICK_ADD_SUCCESS_MESSAGE,
  LABOR_AMEND_QUICK_ADD_SUCCESS_TITLE,
} from "@/encounters/components/EncounterLabor/constants/labor-encounter-amend";
import { showLaborFlowsheetFormNotLoadedToast } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/show-labor-flowsheet-form-not-loaded";
import { useEnqueueLaborAmendPersist } from "@/encounters/components/EncounterLabor/hooks/use-enqueue-labor-amend-persist";
import { useResolveLaborAmendEncounterParentVersionIdForStamp } from "@/encounters/components/EncounterLabor/hooks/use-labor-amend-encounter-parent-version-id";
import {
  addQuestionnaireResponseVersioning,
  createLaborFlowsheetQuestionnaireResponse,
  fetchLaborStageObservations,
  getLaborStageFromMember,
  getOrCreateLaborStage,
  getQuestionnaire,
  getQuestionnaireResponseFromObservation,
} from "@/encounters/components/EncounterLabor/services/labor";
import { updateLaborFlowsheetQuestionnaireResponse } from "@/encounters/components/EncounterLabor/services/update-labor-flowsheet-questionnaire-response";
import {
  hydrateLaborFlowsheetDateTimeFormValuesForEdit,
  normalizeQuestionnaireDateTimeGroupsToUtcParts,
} from "@/encounters/components/EncounterLabor/utils/form-datetime";
import {
  getLaborFlowsheetEditReplaceKey,
  getLaborQuickAddPendingKey,
} from "@/encounters/components/EncounterLabor/utils/labor-amend-pending-keys";
import { formatNormalizedFormValuesForAmendDisplay } from "@/encounters/components/EncounterLabor/utils/labor-amend-preview";
import { isLaborBirthForm } from "@/encounters/components/EncounterLabor/utils/labor-questionnaire-classification";
import {
  dedupeFinalLaborStageObservations,
  getLaborStageAnswerCode,
} from "@/encounters/components/EncounterLabor/utils/labor-stage-dedupe";
import { useLaborAmendFlowsheetPersist } from "@/encounters/contexts/LaborAmendFlowsheetPersistContext";
import { LOINCAnswerCodes } from "@/fhir/enums/loinc";
import { RootState } from "@/store";
import { loadLaborStages, selectCurrentLaborStage } from "@/store/slices/labor-stages";
import { joinReferenceString } from "@/utils/medplum";
import { toast } from "@/utils/notifications";
import { getQuestionnaireResponseFromFormValues } from "@/utils/questionnaires";

export interface LaborFlowsheetDrawerEditContext {
  observation: Observation;
  questionnaireDisplayName?: string;
}

export interface UseLaborFlowsheetDrawerPersistOptions<TFormValues> {
  opened: boolean;
  mode: "quick-add" | "edit";
  onClose: () => void;
  clientId: string;
  encounterId: string;
  questionnaireName: string;
  displayName: string;
  timerKey: string;
  form: UseFormReturnType<TFormValues>;
  parseAndNormalizeFormValues: (values: TFormValues) => TFormValues;
  getDefaultFormValues: () => TFormValues;
  mapFormValuesToQuestionnaireValues: (formValues: TFormValues) => Record<string, unknown>;
  mapQuestionnaireResponseToFormValues: (
    questionnaireResponse: QuestionnaireResponse | undefined,
  ) => TFormValues;
  mergeHydratedEditFormValues: (input: {
    mapped: TFormValues;
    hydratedQuestionnaireValues: Record<string, unknown>;
  }) => TFormValues;
  questionnaireNotFoundMessage: string;
  editEntryNotFoundMessage: string;
  editContext?: LaborFlowsheetDrawerEditContext;
  onRefresh?: () => Promise<void>;
  onAfterPersist?: () => void;
  /** Questionnaire-specific quick-add UI side effects (milestones, tasks, extra timers). */
  runQuickAddLocalSideEffects?: (input: {
    normalizedValues: Record<string, unknown>;
  }) => void | Promise<void>;
  title?: string;
}

export interface UseLaborFlowsheetDrawerPersistResult {
  isLoading: boolean;
  isSubmitting: boolean;
  loadError: string | null;
  questionnaire: Questionnaire | null;
  dateTimeUtcDateAnchors: Record<string, string>;
  drawerLaborStageLoincCode: string | undefined;
  setDrawerLaborStageLoincCode: (laborStageLoincCode: string | undefined) => void;
  showLaborStageInDrawer: boolean;
  drawerTitle: string;
  handleSubmit: (event: FormEvent) => void | Promise<void>;
}

/**
 * Shared load/submit/amend-persist orchestration for native labor flowsheet drawers.
 */
export function useLaborFlowsheetDrawerPersist<TFormValues>({
  opened,
  mode,
  onClose,
  clientId,
  encounterId,
  questionnaireName,
  displayName,
  timerKey,
  form,
  parseAndNormalizeFormValues,
  getDefaultFormValues,
  mapFormValuesToQuestionnaireValues,
  mapQuestionnaireResponseToFormValues,
  mergeHydratedEditFormValues,
  questionnaireNotFoundMessage,
  editEntryNotFoundMessage,
  editContext,
  onRefresh,
  onAfterPersist,
  runQuickAddLocalSideEffects,
  title,
}: UseLaborFlowsheetDrawerPersistOptions<TFormValues>): UseLaborFlowsheetDrawerPersistResult {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const practitioner = profile?.resourceType === "Practitioner" ? profile : null;
  const dispatch = useDispatch();
  const enqueueAmendPersist = useEnqueueLaborAmendPersist();
  const { resetTimer } = useTimerContext();
  const currentLaborStage = useSelector<RootState, Observation | undefined>(
    selectCurrentLaborStage,
  );
  const {
    deferFlowsheetPersists,
    getPendingEntryLaborStageLoincCode,
    getLaborStageObservationsForFlush,
    registerPostAmendmentEffect,
    registerAmendProvenanceTarget,
  } = useLaborAmendFlowsheetPersist();
  const resolveEncounterQuestionnaireParentVersionIdForStamp =
    useResolveLaborAmendEncounterParentVersionIdForStamp();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [dateTimeUtcDateAnchors, setDateTimeUtcDateAnchors] = useState<Record<string, string>>({});
  const [drawerLaborStageLoincCode, setDrawerLaborStageLoincCode] = useState<string | undefined>();
  const [initialQuestionnaireResponse, setInitialQuestionnaireResponse] = useState<
    QuestionnaireResponse | undefined
  >();

  const showLaborStageInDrawer = deferFlowsheetPersists && mode === "quick-add";

  const resolveInitialLaborStageLoincCode = useCallback((): string | undefined => {
    if (isLaborBirthForm(questionnaireName)) {
      return LOINCAnswerCodes.SecondStageLabor;
    }
    if (currentLaborStage) {
      return getLaborStageAnswerCode(currentLaborStage);
    }
    return LOINCAnswerCodes.ProdromalLabor;
  }, [currentLaborStage, questionnaireName]);

  useEffect(() => {
    if (!opened) {
      setIsLoading(false);
      setInitialQuestionnaireResponse(undefined);
      return;
    }

    let isCancelled = false;

    const loadForm = async () => {
      setIsLoading(true);
      setLoadError(null);
      setInitialQuestionnaireResponse(undefined);
      setQuestionnaire(null);
      setDateTimeUtcDateAnchors({});
      try {
        const questionnaireResource = await getQuestionnaire(medplum, questionnaireName);
        if (!questionnaireResource) {
          throw new Error(questionnaireNotFoundMessage);
        }
        if (isCancelled) {
          return;
        }
        setQuestionnaire(questionnaireResource);

        if (mode === "edit") {
          if (!editContext?.observation) {
            throw new Error(editEntryNotFoundMessage);
          }
          const response = await getQuestionnaireResponseFromObservation(
            medplum,
            editContext.observation,
          );
          if (!response) {
            throw new Error(editEntryNotFoundMessage);
          }
          if (isCancelled) {
            return;
          }
          setInitialQuestionnaireResponse(response);
          const mapped = mapQuestionnaireResponseToFormValues(response);
          const questionnaireValues = mapFormValuesToQuestionnaireValues(mapped);
          const hydrated = hydrateLaborFlowsheetDateTimeFormValuesForEdit(
            questionnaireResource,
            questionnaireValues,
            response,
          );
          if (!isCancelled) {
            form.setValues(
              mergeHydratedEditFormValues({
                mapped,
                hydratedQuestionnaireValues: hydrated.values,
              }),
            );
            setDateTimeUtcDateAnchors(hydrated.dateTimeUtcDateAnchors);
          }
        } else if (!isCancelled) {
          setInitialQuestionnaireResponse(undefined);
          form.setValues(getDefaultFormValues());
          setDateTimeUtcDateAnchors({});
          setDrawerLaborStageLoincCode(resolveInitialLaborStageLoincCode());
        }
      } catch (error) {
        console.error(`Error loading ${questionnaireName} form:`, error);
        if (!isCancelled) {
          setLoadError(
            error instanceof Error ? error.message : `Failed to load ${displayName} form.`,
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadForm();

    return () => {
      isCancelled = true;
    };
    // Mapping callbacks are caller-defined; observation id is the stable edit reload signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    opened,
    mode,
    medplum,
    questionnaireName,
    resolveInitialLaborStageLoincCode,
    editContext?.observation.id,
  ]);

  const reloadLaborStagesInRedux = async () => {
    if (onRefresh) {
      await onRefresh();
    }
    const observations = await fetchLaborStageObservations(medplum, clientId, encounterId);
    dispatch(loadLaborStages(dedupeFinalLaborStageObservations(observations)));
  };

  const refreshLaborStages = async () => {
    try {
      await reloadLaborStagesInRedux();
    } catch (error) {
      console.error("Error refreshing labor stages:", error);
    }
  };

  /** Edit saves wait for flowsheet bots before reloading labor stages (matches legacy EditLaborRow). */
  const refreshLaborStagesAfterEditSave = async () => {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    await reloadLaborStagesInRedux();
  };

  const resolveLaborStageObservation = async (
    laborStageLoincCode: string | undefined,
  ): Promise<Observation | undefined> => {
    const stageCode = laborStageLoincCode ?? LOINCAnswerCodes.ProdromalLabor;
    const existingStages = await getLaborStageObservationsForFlush(
      medplum,
      clientId,
      encounterId,
      fetchLaborStageObservations,
    );
    const existingStage = existingStages.find(
      (stage) => getLaborStageAnswerCode(stage) === stageCode,
    );
    if (existingStage) {
      return existingStage;
    }
    return getOrCreateLaborStage(medplum, existingStages, stageCode, clientId, encounterId);
  };

  const buildNormalizedQuestionnaireValues = () => {
    const parsed = parseAndNormalizeFormValues(form.values);
    const questionnaireValues = mapFormValuesToQuestionnaireValues(parsed);
    if (!questionnaire) {
      return questionnaireValues;
    }
    return normalizeQuestionnaireDateTimeGroupsToUtcParts(
      questionnaire,
      questionnaireValues,
      dateTimeUtcDateAnchors,
    );
  };

  const showMissingPractitionerError = () => {
    toast({
      type: "error",
      title: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
      message: "You must be signed in as a practitioner to save this entry.",
      autoClose: false,
    });
  };

  const runQuickAddSuccessSideEffects = async (normalizedValues: Record<string, unknown>) => {
    resetTimer(timerKey);
    await runQuickAddLocalSideEffects?.({ normalizedValues });
    toast({
      type: "success",
      title: LABOR_AMEND_QUICK_ADD_SUCCESS_TITLE,
      message: LABOR_AMEND_QUICK_ADD_SUCCESS_MESSAGE,
      autoClose: 3000,
    });
  };

  const handleQuickAddSubmit = async () => {
    if (!questionnaire?.id) {
      showLaborFlowsheetFormNotLoadedToast();
      return;
    }
    if (!practitioner?.id) {
      showMissingPractitionerError();
      return;
    }

    setIsSubmitting(true);
    let wasAmendPersistEnqueued = false;
    try {
      const normalizedValues = buildNormalizedQuestionnaireValues();
      const selectedLaborStageLoincCode =
        drawerLaborStageLoincCode ?? resolveInitialLaborStageLoincCode();

      const persistFlowsheetQuestionnaire = async (laborStageLoincCodeForPersist?: string) => {
        const stageToUse = await resolveLaborStageObservation(
          laborStageLoincCodeForPersist ?? selectedLaborStageLoincCode,
        );
        const savedQuestionnaireResponse = await createLaborFlowsheetQuestionnaireResponse(
          medplum,
          clientId,
          encounterId,
          practitioner,
          questionnaireName,
          normalizedValues,
          stageToUse ? createReference(stageToUse) : undefined,
          onRefresh,
          undefined,
          resolveEncounterQuestionnaireParentVersionIdForStamp(),
        );

        if (savedQuestionnaireResponse) {
          registerAmendProvenanceTarget(savedQuestionnaireResponse);
        }
        if (stageToUse) {
          registerAmendProvenanceTarget(stageToUse);
        }

        await refreshLaborStages();
      };

      const runPostAmendFlushSideEffects = async () => {
        await runQuickAddSuccessSideEffects(normalizedValues as Record<string, unknown>);
      };

      const runPersistAndEffects = async (laborStageLoincCodeForPersist?: string) => {
        await persistFlowsheetQuestionnaire(laborStageLoincCodeForPersist);
        onAfterPersist?.();
        if (deferFlowsheetPersists) {
          registerPostAmendmentEffect(runPostAmendFlushSideEffects);
          return;
        }
        await runQuickAddSuccessSideEffects(normalizedValues as Record<string, unknown>);
      };

      if (deferFlowsheetPersists) {
        const pendingKey = getLaborQuickAddPendingKey(questionnaireName);
        wasAmendPersistEnqueued = enqueueAmendPersist({
          pendingDisplayEntry: {
            key: pendingKey,
            entryType: "quick-add",
            title: title ?? displayName,
            laborStageLoincCode: selectedLaborStageLoincCode,
            details: formatNormalizedFormValuesForAmendDisplay(
              normalizedValues as Record<string, unknown>,
            ),
          },
          persist: async () => {
            const laborStageLoincCodeAtFlush =
              getPendingEntryLaborStageLoincCode(pendingKey) ?? selectedLaborStageLoincCode;
            await runPersistAndEffects(laborStageLoincCodeAtFlush);
          },
          queuedMessage: LABOR_AMEND_QUEUED_MESSAGE,
          errorTitle: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
          errorMessage: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_QUICK_ADD_MESSAGE,
          onQueued: () => {
            onClose();
          },
        });
        if (wasAmendPersistEnqueued) {
          return;
        }
      }

      await runPersistAndEffects(selectedLaborStageLoincCode);
      onClose();
    } catch (error) {
      console.error(`Error submitting ${questionnaireName} form:`, error);
      toast({
        type: "error",
        title: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
        message:
          error instanceof Error
            ? error.message
            : LABOR_AMEND_FLOWSHEET_SAVE_ERROR_QUICK_ADD_MESSAGE,
        autoClose: false,
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async () => {
    if (mode !== "edit" || !editContext?.observation) {
      onClose();
      return;
    }
    if (!questionnaire?.id) {
      showLaborFlowsheetFormNotLoadedToast();
      return;
    }
    const practitionerId = practitioner?.id;
    if (!practitionerId) {
      showMissingPractitionerError();
      return;
    }

    const normalizedValues = buildNormalizedQuestionnaireValues();

    if (!initialQuestionnaireResponse) {
      toast({
        type: "error",
        title: LABOR_AMEND_FLOWSHEET_EDIT_MISSING_RESPONSE_TITLE,
        message: LABOR_AMEND_FLOWSHEET_EDIT_MISSING_RESPONSE_MESSAGE,
        autoClose: false,
      });
      onClose();
      return;
    }

    const replaceKey = editContext.observation.id
      ? getLaborFlowsheetEditReplaceKey(editContext.observation.id)
      : undefined;

    if (!replaceKey) {
      toast({
        type: "error",
        title: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
        message: editEntryNotFoundMessage,
        autoClose: false,
      });
      onClose();
      return;
    }

    const performPersist = async () => {
      const questionnaireResource = await getQuestionnaire(medplum, questionnaireName);
      if (!questionnaireResource) {
        throw new Error(`Questionnaire ${questionnaireName} not found.`);
      }

      const questionnaireResponseToUpdate =
        (await getQuestionnaireResponseFromObservation(medplum, editContext.observation)) ??
        initialQuestionnaireResponse;

      if (!questionnaireResponseToUpdate) {
        throw new Error("No existing questionnaire response found to update.");
      }

      const currentLaborStageRef = await getLaborStageFromMember(
        medplum,
        editContext.observation,
        clientId,
        encounterId,
      );

      const authorReference = joinReferenceString("Practitioner", practitionerId);
      const patientReference = { reference: joinReferenceString("Patient", clientId) };
      const practitionerReference = createReference(practitioner!);

      const versionedQuestionnaireResponse = addQuestionnaireResponseVersioning(
        {
          ...questionnaireResponseToUpdate,
          item: getQuestionnaireResponseFromFormValues(
            questionnaireResource,
            normalizedValues,
            patientReference,
            practitionerReference,
          ).item,
          authored: new Date().toISOString(),
        },
        questionnaireResponseToUpdate,
        editContext.observation,
        authorReference,
      );

      const stampedQuestionnaireResponse = await updateLaborFlowsheetQuestionnaireResponse(
        medplum,
        {
          questionnaireResponse: versionedQuestionnaireResponse,
          laborStagePartOf: currentLaborStageRef,
          encounterQuestionnaireParentVersionId:
            resolveEncounterQuestionnaireParentVersionIdForStamp(),
        },
      );

      registerAmendProvenanceTarget(stampedQuestionnaireResponse);
      registerAmendProvenanceTarget(editContext.observation);

      onAfterPersist?.();

      if (!deferFlowsheetPersists) {
        toast({
          type: "success",
          title: LABOR_AMEND_QUICK_ADD_SUCCESS_TITLE,
          message: LABOR_AMEND_QUICK_ADD_SUCCESS_MESSAGE,
          autoClose: 3000,
        });

        try {
          await refreshLaborStagesAfterEditSave();
        } catch (refreshError) {
          console.error("Error refreshing labor stages after edit save:", refreshError);
          toast({
            type: "warning",
            title: LABOR_AMEND_LABOR_STAGES_REFRESH_FAILED_TITLE,
            message: LABOR_AMEND_LABOR_STAGES_REFRESH_FAILED_MESSAGE,
            autoClose: false,
          });
        }
      }
    };

    let wasAmendPersistEnqueued = false;
    setIsSubmitting(true);
    try {
      wasAmendPersistEnqueued = enqueueAmendPersist({
        replaceKey,
        pendingDisplayEntry: {
          key: replaceKey,
          entryType: "flowsheet-edit",
          title: editContext.questionnaireDisplayName ?? displayName,
          details: formatNormalizedFormValuesForAmendDisplay(
            normalizedValues as Record<string, unknown>,
          ),
        },
        persist: performPersist,
        queuedMessage: LABOR_AMEND_QUEUED_MESSAGE,
        errorTitle: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
        errorMessage: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_EDIT_ROW_MESSAGE,
        onQueued: onClose,
      });
      if (wasAmendPersistEnqueued) {
        return;
      }

      await performPersist();
      onClose();
    } catch (error) {
      console.error(`Error updating ${questionnaireName} row:`, error);
      toast({
        type: "error",
        title: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
        message:
          error instanceof Error
            ? error.message
            : LABOR_AMEND_FLOWSHEET_SAVE_ERROR_EDIT_ROW_MESSAGE,
        autoClose: false,
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (isSubmitting || isLoading || loadError) {
      return;
    }
    if (mode === "edit" && !initialQuestionnaireResponse) {
      return;
    }
    const validation = form.validate();
    if (validation.hasErrors) {
      return;
    }

    if (mode === "quick-add") {
      await handleQuickAddSubmit();
      return;
    }

    await handleEditSubmit();
  };

  const drawerTitle =
    title ??
    (mode === "edit" ? (editContext?.questionnaireDisplayName ?? displayName) : displayName);

  return {
    isLoading,
    isSubmitting,
    loadError,
    questionnaire,
    dateTimeUtcDateAnchors,
    drawerLaborStageLoincCode,
    setDrawerLaborStageLoincCode,
    showLaborStageInDrawer,
    drawerTitle,
    handleSubmit,
  };
}
